/**
 * CoreREST API Client
 *
 * Handles all HTTP communication with the StreamSyncEngage backend API.
 *
 * NOTE: JWT token injection is handled automatically by Neodigm 55's mvvLegit
 * mvvLegit overloads the global fetch() function to automatically inject Bearer tokens
 * for all requests to the configured BASE URL.
 *
 * Features:
 * - Error handling and token expiration detection
 * - JSON request/response handling
 * - Integration with pub/sub event system
 */

import PubSub from 'pubsub-js';
import AppBroadcast from '../../../../shared/appBroadcast.js';
import { guardedFetch } from './navGeneration.js';

// API Base URL - client ALWAYS uses production API
// RULE: All client apps point to hosted API (no localhost fallback)
const API_BASE_URL = 'https://machfivemagnet-saas.onrender.com';

/**
 * CoreREST API Client Class
 */
class CoreRestAPI {
  constructor() {
    this.baseUrl = API_BASE_URL;
  }

  /**
   * Generic fetch wrapper with error handling
   *
   * NOTE: Bearer token is automatically injected by mvvLegit's fetch overload
   */
  async request(endpoint, options = {}) {
    const url = `${this.baseUrl}${endpoint}`;

    // Default headers
    const headers = {
      'Content-Type': 'application/json',
      ...options.headers
    }

    try {
      const response = await guardedFetch(url, {
        ...options,
        headers
      });

      // 401 ONLY. A 403 is NOT an expired session and must not be reported as one.
      //
      // This branch publishes AUTH_TOKEN_EXPIRED, whose handler toasts "Session expired.
      // Please sign in again." (app_events.js:195). Firing that on a 403 tells a user
      // their session died when in fact they were refused a resource they are still
      // signed in for — a worse message than "try again", because it is a false claim
      // about their session rather than useless advice about the failure.
      //
      // A 403 now falls through to the normal return below, which already carries
      // { ok, status, data } — so a caller can ask isDenial(result) directly. This is a
      // generic wrapper: its `endpoint` is a parameter, so it stands in for any resource
      // and cannot keep a blanket auth reading. In practice its reach today is small —
      // forgotPassword() is the only live call and /acctEntity/forgot is unauthenticated
      // — but every future caller inherits whatever this branch says.
      if (response.status === 401) {
        PubSub.publish(AppBroadcast.AUTH_TOKEN_EXPIRED, {
          status: response.status,
          endpoint
        });

        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Authentication failed');
      }

      // Parse JSON response
      const data = await response.json();

      // Return response with status info
      return {
        ok: response.ok,
        status: response.status,
        data
      };

    } catch (error) {
      console.error('API Request Error:', error);

      // Publish error event
      PubSub.publish(AppBroadcast.APP_ERROR, {
        message: error.message,
        endpoint
      });

      throw error;
    }
  }

  /**
   * GET request
   */
  async get(endpoint) {
    return this.request(endpoint, {
      method: 'GET'
    });
  }

  /**
   * POST request
   */
  async post(endpoint, body) {
    return this.request(endpoint, {
      method: 'POST',
      body: JSON.stringify(body)
    });
  }

  /**
   * PUT request
   */
  async put(endpoint, body) {
    return this.request(endpoint, {
      method: 'PUT',
      body: JSON.stringify(body)
    });
  }

  /**
   * DELETE request
   */
  async delete(endpoint) {
    return this.request(endpoint, {
      method: 'DELETE'
    });
  }

  // ============================================
  // Authentication API Methods
  // ============================================

  /**
   * Sign in
   */
  async signin(email, passwordHash) {
    return this.post('/api/acctEntity/signin', {
      email,
      hash: passwordHash
    });
  }

  /**
   * Sign up (create new account)
   */
  async signup(accountData) {
    return this.post('/api/acctEntity/signup', accountData);
  }

  /**
   * Sign out
   */
  async signout() {
    return this.post('/api/acctEntity/signout', {});
  }

  /**
   * Request password reset (forgot password)
   */
  async forgotPassword(email) {
    return this.get(`/m5t/v5/acctEntity/forgot?CODE=${encodeURIComponent(email)}`);
  }

  /**
   * Reset password hash
   */
  async resetHash(email, newPasswordHash) {
    return this.post('/api/acctEntity/resethash', {
      email,
      hash: newPasswordHash
    });
  }

  /**
   * Verify account email
   */
  async verifyAccount(code) {
    return this.get(`/api/acctEntity/verify?CODE=${code}`);
  }

  // ============================================
  // Account Management API Methods
  // ============================================

  /**
   * Get all accounts (requires auth)
   */
  async getAccounts() {
    return this.get('/api/acctEntity');
  }

  /**
   * Get account by ID (requires auth)
   */
  async getAccount(id) {
    return this.get(`/api/acctEntity/${id}`);
  }

  /**
   * Update account (requires auth)
   */
  async updateAccount(id, accountData) {
    return this.put(`/api/acctEntity/${id}`, accountData);
  }

  /**
   * Delete account (requires auth)
   */
  async deleteAccount(id) {
    return this.delete(`/api/acctEntity/${id}`);
  }

  // ============================================
  // Health Check
  // ============================================

  /**
   * Ping server (health check)
   */
  async ping() {
    return this.get('/ping');
  }
}

// Export singleton instance
export default new CoreRestAPI();
